import { api, apiBlob } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  CreateIssuanceRequest,
  IssuableItem,
  IssuableSource,
  IssuableUnit,
  IssuanceDetail,
  IssuanceListItem,
  IssuanceListQuery,
  IssuanceType,
  IssuedAssetLine,
  PropertyClassification,
  RecordIssuanceReturnRequest,
} from './issuanceTypes';

/**
 * Serializes the base list query.
 *
 * Every facet is written whenever its KEY IS PRESENT, empty string included — an empty value is
 * the reader saying "none of these", which matches no row. A truthiness check would drop it and
 * the server would read the axis as unmentioned, which is the opposite result.
 */
function base(query: ListQuery, params: URLSearchParams): URLSearchParams {
  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  if (query.status !== undefined) params.set('status', query.status);

  return params;
}

function issuanceQuery(query: IssuanceListQuery): string {
  const params = base(query, new URLSearchParams());

  if (query.type !== undefined) params.set('type', query.type);
  if (query.stage !== undefined) params.set('stage', query.stage);
  if (query.facility !== undefined) params.set('facility', query.facility);
  if (query.attention !== undefined) params.set('attention', query.attention);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

/**
 * Stock leaving a store.
 *
 * <b>There is no `update`.</b> An issuance records what physically happened; the two things
 * worth changing afterwards are a return (the stock came back) and a delete (the slip was keyed
 * against the wrong record), and each is its own call under its own key so the row says which of
 * them occurred.
 *
 * <b>The three picker feeds carry `issuances.create`, not `read`.</b> They disclose what is in
 * stock and where, which is a wider fact than the list of past issuances — a reader who may only
 * look at the register has no business enumerating the stores' contents through it.
 */
/**
 * The acknowledgement, as a query segment.
 *
 * <b>ABSENT rather than empty when no form is being signed.</b> A present-but-empty parameter
 * is a different thing from an absent one on this product’s own facet contract, and the model
 * binder would read it as a failed conversion rather than as null — so a non-custody feed
 * sends nothing and every class stays offered.
 */
const ackParam = (acknowledgementType: PropertyClassification | null | undefined) =>
  acknowledgementType === null || acknowledgementType === undefined
    ? ''
    : `&acknowledgementType=${acknowledgementType}`;

export const issuancesApi = {
  list: (query: IssuanceListQuery, signal?: AbortSignal) =>
    api<PagedResult<IssuanceListItem>>(`/api/issuances${issuanceQuery(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<IssuanceDetail>(`/api/issuances/${id}`, { signal }),

  /**
   * Every article issued to one employee (accountable officer, else recipient), deleted slips included — the
   * personnel profile's Issued Assets panel filters them in memory, so its Show deleted costs
   * no second request. `issuances.read`.
   */
  issuedTo: (personnelId: string, signal?: AbortSignal) =>
    api<IssuedAssetLine[]>(`/api/issuances/issued-to/${personnelId}?includeDeleted=true`, { signal }),

  /** What can be issued under this method right now. Out-of-stock items never appear. */
  issuableItems: (
    issuanceType: IssuanceType,
    acknowledgementType?: PropertyClassification | null,
    signal?: AbortSignal,
  ) =>
    api<IssuableItem[]>(
      `/api/issuances/issuable-items?issuanceType=${issuanceType}${ackParam(acknowledgementType)}`,
      { signal },
    ),

  /** Where one item can be drawn from, and how much is at each store. */
  issuableSources: (
    inventoryItemId: string,
    acknowledgementType?: PropertyClassification | null,
    signal?: AbortSignal,
  ) =>
    api<IssuableSource[]>(
      `/api/issuances/issuable-items/${inventoryItemId}/sources?${ackParam(acknowledgementType).slice(1)}`,
      { signal },
    ),

  /** The units of one serialized item that are free to hand over. */
  /**
   * EVERY unit this method can release, across every item.
   *
   * A serialized issue is ONE NAMED UNIT, so the form’s row picks a unit — and a picker over
   * units cannot be fed per item without the reader first answering the question it exists to
   * ask. One call, rather than one per serialized item in the catalog.
   */
  issuableUnitsFor: (
    issuanceType: IssuanceType,
    acknowledgementType?: PropertyClassification | null,
    signal?: AbortSignal,
  ) =>
    api<IssuableUnit[]>(
      `/api/issuances/issuable-units?issuanceType=${issuanceType}${ackParam(acknowledgementType)}`,
      { signal },
    ),

  issuableUnits: (
    inventoryItemId: string,
    acknowledgementType?: PropertyClassification | null,
    signal?: AbortSignal,
  ) =>
    api<IssuableUnit[]>(
      `/api/issuances/issuable-items/${inventoryItemId}/units?${ackParam(acknowledgementType).slice(1)}`,
      { signal },
    ),

  /** Releases stock. Moves every quantity through the ledger and allocates the numbers. */
  create: (request: CreateIssuanceRequest) =>
    api<IssuanceDetail>('/api/issuances', { method: 'POST', body: request }),

  /** Records stock coming back against one line of a custody issuance. */
  recordReturn: (id: string, request: RecordIssuanceReturnRequest) =>
    api<IssuanceDetail>(`/api/issuances/${id}/returns`, { method: 'POST', body: request }),

  /**
   * Hides a slip keyed against the wrong record.
   *
   * It does NOT put the stock back — the server refuses one with custody stock still out, and
   * the dialog says so.
   */
  deactivate: (id: string) => api<void>(`/api/issuances/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/issuances/${id}/restore`, { method: 'POST' }),

  /**
   * The ICS or PAR the slip was signed against, as a PDF — drawn by the server on request and
   * never stored. Names on it ride `personnel.read` and the reader's access level, the posts
   * `plantilla.read`, the office `org-units.read`, exactly as on the profile.
   *
   * Use `issuanceForm.ts` rather than this directly: it re-types the blob, which is what makes
   * it safe to frame.
   */
  form: (id: string, signal?: AbortSignal) => apiBlob(`/api/issuances/${id}/form`, { signal }),
};
